import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Brush,
    ReferenceLine,
} from 'recharts';
import './EnvironmentalViewer.css';
import { API_BASE } from '../../config';

// ── Variable display labels ──────────────────────────────────────────────────
const VARIABLE_LABELS = {
    water_level: 'Water Level (ft)',
    water_temperature: 'Water Temperature (°F)',
    air_temperature: 'Air Temperature (°F)',
    wind_speed: 'Wind Speed (kts)',
    wind_direction: 'Wind Direction (°)',
    wind_gust: 'Wind Gust (kts)',
    barometric_pressure: 'Barometric Pressure (mb)',
    discharge: 'Discharge',
    gage_height: 'Gage Height',
    visibility: 'Visibility',
    precipitation: 'Precipitation',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function toLocalInputValue(date) {
    const pad = (n) => String(n).padStart(2, '0');
    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}`
    );
}

function toCleanISOString(date) {
    return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

function formatTickLabel(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${month}/${day} ${hours}:${minutes}`;
}

function formatTooltipLabel(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
    });
}

function computeTickInterval(dataLength) {
    if (dataLength <= 30) return 1;
    if (dataLength <= 120) return Math.ceil(dataLength / 20);
    if (dataLength <= 500) return Math.ceil(dataLength / 25);
    return Math.ceil(dataLength / 30);
}

// ── Custom tooltip ───────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label }) {
    if (!active || !payload || payload.length === 0) return null;

    return (
        <div className="env-chart-tooltip">
            <p className="env-chart-tooltip-time">{formatTooltipLabel(label)}</p>
            {payload.map((entry, idx) => (
                <p key={idx} className="env-chart-tooltip-value" style={{ color: entry.color }}>
                    {entry.name}: <strong>{entry.value != null ? entry.value.toFixed(3) : '—'}</strong>
                </p>
            ))}
        </div>
    );
}

// ── Main component ───────────────────────────────────────────────────────────

export default function EnvironmentalViewer() {
    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    // ── Station catalogue (fetched from /api/environmental/stations) ──
    const [stations, setStations] = useState([]);
    const [stationsLoading, setStationsLoading] = useState(true);
    const [stationsError, setStationsError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        async function fetchStations() {
            setStationsLoading(true);
            setStationsError(null);

            const url = `${API_BASE}/api/environmental/stations`;
            console.log('[ENV] Fetching station catalogue:', url);

            try {
                const resp = await fetch(url);
                console.log('[ENV] Stations response: HTTP', resp.status);

                if (!resp.ok) {
                    const body = await resp.json().catch(() => null);
                    throw new Error(body?.error || `HTTP ${resp.status}`);
                }
                const data = await resp.json();
                console.log('[ENV] Stations loaded:', data.length, 'stations');
                console.log('[ENV] Station list:', data.map((s) => `${s.name} (${s.station_id})`));

                if (!cancelled) {
                    setStations(Array.isArray(data) ? data : []);
                }
            } catch (err) {
                console.error('[ENV] Failed to load stations:', err);
                if (!cancelled) {
                    setStationsError(err.message);
                }
            } finally {
                if (!cancelled) setStationsLoading(false);
            }
        }

        fetchStations();
        return () => { cancelled = true; };
    }, []);

    // ── Form state ───────────────────────────────────────────────────
    const [stationId, setStationId] = useState('');
    const [variable, setVariable] = useState('');
    const [startTime, setStartTime] = useState(toLocalInputValue(yesterday));
    const [endTime, setEndTime] = useState(toLocalInputValue(now));

    const [observations, setObservations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastQuery, setLastQuery] = useState(null);

    // ── Auto-select first station once loaded ────────────────────────
    useEffect(() => {
        if (stations.length > 0 && !stationId) {
            setStationId(stations[0].station_id);
        }
    }, [stations, stationId]);

    // ── Derive current station + filtered variables ──────────────────
    const selectedStation = useMemo(
        () => stations.find((s) => s.station_id === stationId) || null,
        [stations, stationId],
    );

    const availableVariables = useMemo(() => {
        if (!selectedStation || !selectedStation.variables) return [];
        return selectedStation.variables.map((v) => ({
            value: v,
            label: VARIABLE_LABELS[v] || v,
        }));
    }, [selectedStation]);

    // Reset variable when station changes and current variable isn't valid
    useEffect(() => {
        if (availableVariables.length === 0) {
            setVariable('');
            return;
        }
        const stillValid = availableVariables.some((v) => v.value === variable);
        if (!stillValid) {
            setVariable(availableVariables[0].value);
        }
    }, [availableVariables, variable]);

    // ── Fetch data ───────────────────────────────────────────────────

    const handleLoadData = useCallback(
        async (overrideRange = false) => {
            // ── Log UI state ─────────────────────────────────────────
            console.group('[ENV] ═══ Load Data clicked ═══');
            console.log('[ENV] UI inputs:', {
                station_id: stationId,
                station_name: selectedStation?.name || '(unknown)',
                variable,
                variable_label: VARIABLE_LABELS[variable] || variable,
                start_time: startTime,
                end_time: endTime,
            });

            if (!stationId || !variable || !startTime || !endTime) {
                console.warn('[ENV] Missing fields — aborting');
                console.groupEnd();
                setError('Please fill in all fields.');
                return;
            }

            const start = new Date(startTime);
            const end = new Date(endTime);

            if (isNaN(start.getTime())) {
                console.warn('[ENV] Invalid start time:', startTime);
                console.groupEnd();
                setError('Invalid start time.');
                return;
            }
            if (isNaN(end.getTime())) {
                console.warn('[ENV] Invalid end time:', endTime);
                console.groupEnd();
                setError('Invalid end time.');
                return;
            }
            if (end <= start) {
                console.warn('[ENV] end <= start:', { start: start.toISOString(), end: end.toISOString() });
                console.groupEnd();
                setError('End time must be after start time.');
                return;
            }

            const cleanStart = toCleanISOString(start);
            const cleanEnd = toCleanISOString(end);

            console.log('[ENV] Parsed timestamps:', {
                start_raw: startTime,
                start_iso: cleanStart,
                end_raw: endTime,
                end_iso: cleanEnd,
                span_hours: ((end - start) / 3600000).toFixed(1),
            });

            setLoading(true);
            setError(null);
            setObservations([]);

            const params = new URLSearchParams({
                station_id: stationId,
                variable,
                start_time: cleanStart,
                end_time: cleanEnd,
            });

            const url = `${API_BASE}/api/environmental?${params}`;
            console.log('[ENV] Request URL:', url);

            try {
                const t0 = performance.now();
                const response = await fetch(url);
                const elapsed = (performance.now() - t0).toFixed(0);

                console.log('[ENV] Response:', {
                    status: response.status,
                    statusText: response.statusText,
                    elapsed_ms: elapsed,
                });

                if (!response.ok) {
                    const body = await response.json().catch(() => null);
                    console.error('[ENV] Error response body:', body);
                    throw new Error(body?.error || `HTTP ${response.status}`);
                }

                const data = await response.json();

                console.log('[ENV] Response payload:', {
                    station_id: data.station_id,
                    variable: data.variable,
                    source: data.source || '(not set)',
                    observation_count: (data.observations || []).length,
                });

                if (data.observations && data.observations.length > 0) {
                    console.log('[ENV] First observation:', data.observations[0]);
                    console.log('[ENV] Last observation:', data.observations[data.observations.length - 1]);
                } else {
                    console.warn('[ENV] No observations returned');
                }

                setObservations(data.observations || []);
                setLastQuery({
                    stationId: data.station_id,
                    variable: data.variable,
                    stationName: selectedStation?.name || stationId,
                    variableLabel: VARIABLE_LABELS[data.variable] || data.variable,
                    count: (data.observations || []).length,
                    source: data.source || 'unknown',
                });
            } catch (err) {
                console.error('[ENV] Fetch error:', err);
                setError(err.message || 'Failed to load environmental data.');
            } finally {
                setLoading(false);
                console.groupEnd();
            }
        },
        [stationId, variable, startTime, endTime, selectedStation],
    );

    const handleReloadData = () => {
        handleLoadData(true);
    };

    // ── Derived chart data ───────────────────────────────────────────

    const chartData = useMemo(
        () =>
            observations.map((obs) => ({
                timestamp: obs.timestamp,
                value: obs.value,
            })),
        [observations],
    );

    const meanValue = useMemo(() => {
        if (chartData.length === 0) return null;
        const sum = chartData.reduce((acc, d) => acc + (d.value ?? 0), 0);
        return sum / chartData.length;
    }, [chartData]);

    const tickInterval = useMemo(() => computeTickInterval(chartData.length), [chartData.length]);

    // ── Render ───────────────────────────────────────────────────────

    return (
        <div className="env-viewer">
            {/* ── Controls ─────────────────────────────────────────── */}
            <div className="env-viewer-controls">
                <h3 className="env-viewer-title">Environmental Observations</h3>

                {stationsLoading && (
                    <div className="env-message env-loading">Loading stations…</div>
                )}
                {stationsError && (
                    <div className="env-message env-error">
                        ❌ Failed to load stations: {stationsError}
                    </div>
                )}

                <div className="env-viewer-form">
                    <div className="env-field">
                        <label htmlFor="env-station">Station</label>
                        <select
                            id="env-station"
                            value={stationId}
                            onChange={(e) => setStationId(e.target.value)}
                            disabled={stationsLoading || stations.length === 0}
                        >
                            {stations.length === 0 && !stationsLoading ? (
                                <option value="">-- No stations available --</option>
                            ) : (
                                <>
                                    <option value="">-- Select Station --</option>
                                    {stations.map((s) => (
                                        <option key={s.station_id} value={s.station_id}>
                                            {s.name} ({s.station_id})
                                        </option>
                                    ))}
                                </>
                            )}
                        </select>
                    </div>

                    <div className="env-field">
                        <label htmlFor="env-variable">Variable</label>
                        <select
                            id="env-variable"
                            value={variable}
                            onChange={(e) => setVariable(e.target.value)}
                            disabled={availableVariables.length === 0}
                        >
                            {availableVariables.length === 0 ? (
                                <option value="">-- Select a station first --</option>
                            ) : (
                                availableVariables.map((v) => (
                                    <option key={v.value} value={v.value}>
                                        {v.label}
                                    </option>
                                ))
                            )}
                        </select>
                    </div>

                    <div className="env-field">
                        <label htmlFor="env-start">Start Time</label>
                        <input
                            id="env-start"
                            type="datetime-local"
                            value={startTime}
                            onChange={(e) => setStartTime(e.target.value)}
                        />
                    </div>

                    <div className="env-field">
                        <label htmlFor="env-end">End Time</label>
                        <input
                            id="env-end"
                            type="datetime-local"
                            value={endTime}
                            onChange={(e) => setEndTime(e.target.value)}
                        />
                    </div>

                    <button
                        className="env-load-btn"
                        onClick={handleLoadData}
                        disabled={loading || !stationId || !variable}
                    >
                        {loading ? 'Loading…' : 'Load Data'}
                    </button>
                </div>

                {/* Quick-range shortcuts */}
                <div className="env-quick-ranges">
                    {[
                        { label: '6 h', hours: 6 },
                        { label: '24 h', hours: 24 },
                        { label: '3 d', hours: 72 },
                        { label: '7 d', hours: 168 },
                    ].map(({ label, hours }) => (
                        <button
                            key={label}
                            className="env-quick-btn"
                            onClick={() => {
                                const n = new Date();
                                setEndTime(toLocalInputValue(n));
                                setStartTime(
                                    toLocalInputValue(new Date(n.getTime() - hours * 3600000)),
                                );
                            }}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/* Data source indicator */}
                {lastQuery?.source && (
                    <div className="env-source-tag">
                        Source: <strong>{lastQuery.source}</strong>
                    </div>
                )}
            </div>

            {/* ── Status area ──────────────────────────────────────── */}
            <div className="env-viewer-content">
                {error && (
                    <div className="env-message env-error">
                        ❌ {error}
                    </div>
                )}

                {loading && (
                    <div className="env-message env-loading">
                        Fetching observations…
                    </div>
                )}

                {!loading && !error && lastQuery && chartData.length === 0 && (
                    <div className="env-message env-empty">
                        No data available for <strong>{lastQuery.stationName}</strong> /{' '}
                        <strong>{lastQuery.variableLabel}</strong> in the selected range.
                    </div>
                )}

                {/* ── Chart ────────────────────────────────────────── */}
                {chartData.length > 0 && lastQuery && (
                    <>
                        <div className="env-chart-header">
                            <span className="env-chart-label">
                                {lastQuery.stationName} &mdash; {lastQuery.variableLabel}
                            </span>
                            <span className="env-chart-count">
                                {lastQuery.count.toLocaleString()} observations
                            </span>
                        </div>

                        <div className="env-chart-wrapper">
                            <ResponsiveContainer width="100%" height={400}>
                                <LineChart
                                    data={chartData}
                                    margin={{ top: 10, right: 110, left: 40, bottom: 20 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />

                                    <XAxis
                                        dataKey="timestamp"
                                        tickFormatter={formatTickLabel}
                                        interval={tickInterval}
                                        tick={{ fontSize: 11 }}
                                        angle={-35}
                                        textAnchor="end"
                                        height={60}
                                        padding={{ left: 20, right: 20 }}
                                    />

                                    <YAxis
                                        domain={['auto', 'auto']}
                                        tick={{ fontSize: 12 }}
                                        label={{
                                            value: lastQuery.variableLabel,
                                            angle: -90,
                                            position: 'insideLeft',
                                            style: { textAnchor: 'middle', fontSize: 12 },
                                        }}
                                    />

                                    <Tooltip content={<ChartTooltip />} />

                                    <Legend
                                        formatter={() =>
                                            `${lastQuery.stationName} — ${lastQuery.variableLabel}`
                                        }
                                    />

                                    {meanValue !== null && (
                                        <ReferenceLine
                                            y={meanValue}
                                            stroke="#ff9800"
                                            strokeDasharray="6 4"
                                            label={{
                                                value: `mean ${meanValue.toFixed(2)}`,
                                                fill: '#ff9800',
                                                fontSize: 11,
                                                position: 'right',
                                                offset: 10,
                                            }}
                                        />
                                    )}

                                    <Line
                                        type="monotone"
                                        dataKey="value"
                                        name={lastQuery.variableLabel}
                                        stroke="#2196F3"
                                        strokeWidth={1.5}
                                        dot={false}
                                        activeDot={{ r: 4, strokeWidth: 0 }}
                                        isAnimationActive={chartData.length < 2000}
                                    />

                                    <Brush
                                        dataKey="timestamp"
                                        height={28}
                                        stroke="#2196F3"
                                        tickFormatter={formatTickLabel}
                                        travellerWidth={14}
                                        padding={{ left: 20, right: 20 }}
                                        gap={5}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Summary stats */}
                        <div className="env-stats">
                            <div className="env-stat">
                                <span className="env-stat-label">Min</span>
                                <span className="env-stat-value">
                                    {Math.min(...chartData.map((d) => d.value)).toFixed(3)}
                                </span>
                            </div>
                            <div className="env-stat">
                                <span className="env-stat-label">Max</span>
                                <span className="env-stat-value">
                                    {Math.max(...chartData.map((d) => d.value)).toFixed(3)}
                                </span>
                            </div>
                            <div className="env-stat">
                                <span className="env-stat-label">Mean</span>
                                <span className="env-stat-value">
                                    {meanValue?.toFixed(3) ?? '—'}
                                </span>
                            </div>
                            <div className="env-stat">
                                <span className="env-stat-label">Points</span>
                                <span className="env-stat-value">
                                    {chartData.length.toLocaleString()}
                                </span>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}